import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Transaction, WalletType } from "../../models/Transactions";
import { exchangeRateRepository } from "../../repositories/ExchangeRateRepository";
import { getCurrencyCode } from "../../utils/Utils";
import { RootState } from "../index";
import { doesPaymentExists } from "../../utils/BreezSpark";

interface WalletTransactionSearchState {
  transactions: Transaction[];
}

const initialState: WalletTransactionSearchState = {
  transactions: [],
};

const enrichTransactionWithFiat = (transaction: Transaction): Transaction => {
  if (transaction.amountFiat !== undefined && transaction.currencyCodeFiat !== undefined) {
    return transaction;
  }

  const currencyCode = getCurrencyCode();
  const exchangeRate = exchangeRateRepository.getCachedExchangeRateForSat(currencyCode);

  if (exchangeRate === null) {
    return transaction;
  }

  return {
    ...transaction,
    amountFiat: transaction.amount * exchangeRate,
    feeFiat: transaction.fee ? transaction.fee * exchangeRate : undefined,
    opaFeeFiat: transaction.opaFee ? transaction.opaFee * exchangeRate : undefined,
    routingFeeFiat: transaction.routingFee ? transaction.routingFee * exchangeRate : undefined,
    currencyCodeFiat: currencyCode,
  };
};

export const storeSearchTransactions = createAsyncThunk(
  'walletTransactionSearch/storeSearchTransactions',
  async (transactions: Transaction[]) => {
    const checkedTransactions = await Promise.all(
      transactions.map(async (transaction) => {
        if (transaction.walletType !== WalletType.SPARK) {
          return transaction;
        }

        try {
          const exists = await doesPaymentExists(transaction.breezTxId ??transaction.paymentHash);
          return exists ? transaction : null;
        } catch (_error) {
          return null;
        }
      })
    );

    return checkedTransactions.filter((transaction): transaction is Transaction => transaction !== null);
  }
);

export const getSearchTransactionByPaymentHash = createAsyncThunk(
  'walletTransactionSearch/getSearchTransactionByPaymentHash',
  async (paymentHash: string, { getState }) => {
    const state = getState() as RootState;
    return state.walletTransactionSearch.transactions.find(
      (transaction) => transaction.paymentHash === paymentHash
    ) ?? null;
  }
);

const walletTransactionSearchSlice = createSlice({
  name: 'walletTransactionSearch',
  initialState,
  reducers: {
    resetSearchTransactions: (state) => {
      state.transactions = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(storeSearchTransactions.fulfilled, (state, action) => {
      action.payload.forEach((transaction) => {
        const enrichedTransaction = enrichTransactionWithFiat(transaction);

        const existingIndex = state.transactions.findIndex(
          (currentTransaction) => currentTransaction.paymentHash === enrichedTransaction.paymentHash
        );

        if (existingIndex !== -1) {
          state.transactions[existingIndex] = enrichedTransaction;
          return;
        }

        state.transactions.push(enrichedTransaction);
      });
    });
  },
});

export const { resetSearchTransactions } = walletTransactionSearchSlice.actions;

export default walletTransactionSearchSlice.reducer;
